import {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
} from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { Card, Col, Typography, Spin } from "antd";

import showAlertNotification from "components/common/Modal/AlertNotification";
import { Field } from "components/staff/Field/Field";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { useParams, useSearchParams } from "react-router-dom";
import UploadFileCommon from "components/common/Upload/UploadFileCommon";

import {
  useGetAllContractsQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
} from "services/contract.service";

import {
  ContractAllowanceRecord,
  ContractRecord,
  CreateContractPayload,
} from "types/contract.types";
import { TableEditCommon } from "components/common";
import { TableEditColumn } from "types/table.types";

const { Title, Text } = Typography;

interface ContractInfoTabProps {
  isEditing?: boolean;
}

export interface ContractInfoTabHandle {
  submitContract: () => Promise<void>;
}

interface FileData {
  id?: string;
  filePath: string;
  name: string;
  order: number;
}

const ContractInfoTab = forwardRef<ContractInfoTabHandle, ContractInfoTabProps>(
  (props, ref) => {
  const { isEditing = false } = props;

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const employeeType = searchParams.get("type") || "staff";

  // Gọi API lấy hợp đồng hiện tại của nhân viên/dài xe dựa trên ID và loại nhân viên (staff/driver)
  const queryFilter =
    employeeType === "staff"
      ? { staffId: id, include: "allowances|documents|documents.document" }
      : { driverId: id, include: "allowances|documents|documents.document" };
  const { data: contractRes, isLoading: isFetching } = useGetAllContractsQuery(
    { query: queryFilter },
    { skip: !id },
  );

  const currentContract = contractRes?.data?.rows?.[0];

  const [createContract] = useCreateContractMutation();
  const [updateContract] = useUpdateContractMutation();

  const [contractData, setContractData] = useState({
    contractNumber: "",
    startDate: "",
    contractType: "",
    endDate: "",
    joinDate: "",
    position: "",
    jobTitle: "",
    basicSalary: "",
    workRequirement: "",
  });

  const [allowanceData, setAllowanceData] = useState<ContractAllowanceRecord[]>(
    [],
  );
  const [note, setNote] = useState("");
  const [filesData, setFilesData] = useState<FileData[]>([]);

  // Đổ dữ liệu hợp đồng hiện tại vào state khi có dữ liệu mới từ API
  useEffect(() => {
    if (currentContract) {
      setContractData({
        contractNumber: currentContract.contractNumber || "",
        startDate: currentContract.startDate || "",
        contractType: currentContract.contractType || "",
        endDate: currentContract.endDate || "",
        joinDate: currentContract.joinDate || "",
        position: currentContract.position || "",
        jobTitle: currentContract.jobTitle || "",
        basicSalary: currentContract.basicSalary?.toString() || "",
        workRequirement: currentContract.workRequirement || "",
      });

      const mappedAllowances =
        currentContract.allowances?.map((item) => ({
          ...item,
          key: item.id,
        })) || [];

      setAllowanceData(mappedAllowances);

      // Map documents từ BE vào filesData của UploadFileCommon
      if (currentContract.documents && currentContract.documents.length > 0) {
        const mappedFiles = currentContract.documents.map((doc, idx) => ({
          id: doc.documentId,
          filePath: doc.document?.fileUrl || "",
          name: doc.document?.fileName || `document-${idx + 1}`,
          order: idx + 1,
        }));
        setFilesData(mappedFiles);
        setNote(currentContract.documents[0]?.note || "");
      } else {
        setFilesData([]);
      }
    }
  }, [currentContract]);

  // Định nghĩa hàm submitContract để được gọi từ component cha khi lưu hợp đồng
  useImperativeHandle(ref, () => ({
    submitContract: async () => {
      try {
        const payload = {
          ...contractData,
          basicSalary: Number(contractData.basicSalary),
          ...(employeeType === "staff" ? { staffId: id } : { driverId: id }),

          ...(allowanceData.length > 0
            ? {
                allowances: allowanceData.map((item) => ({
                  ...(item.id ? { id: item.id } : {}),
                  name: item.name,
                  amount: Number(item.amount),
                  unit: item.unit,
                  note: item.note,
                })),
              }
            : {}),
          // Map filesData thành documents payload cho API
          documents: filesData
            .filter((file) => !!file.id)
            .map((file) => ({
              documentId: file.id!,
              note: note,
            })),
        };

        if (currentContract?.id) {
          await updateContract({
            id: currentContract.id,
            ...(payload as unknown as Partial<ContractRecord>),
          }).unwrap();
          showAlertNotification({
            type: "success",
            title: "Thành công",
            message: "Đã cập nhật hợp đồng",
          });
        } else {
          await createContract(
            payload as unknown as CreateContractPayload,
          ).unwrap();
          showAlertNotification({
            type: "success",
            title: "Thành công",
            message: "Đã tạo hợp đồng mới",
          });
        }
      } catch (error) {
        showAlertNotification({
          type: "error",
          title: "Lỗi",
          message: "Không thể lưu hợp đồng",
        });
      }
    },
  }));

  const handleFieldUpdate = (path: string, value: string) => {
    setContractData((prev) => ({ ...prev, [path]: value }));
  };

  const handleChangeData = (newData: ContractAllowanceRecord[]) => {
    setAllowanceData(newData);
  };

  const handleSaveData = (
    key: string | number,
    record: ContractAllowanceRecord,
  ) => {
    setAllowanceData((prev) =>
      prev.map((item) => (item.key === key ? record : item)),
    );
  };

  const handleDeleteRow = (key: string | number) => {
    setAllowanceData((prev) => prev.filter((item) => item.key !== key));
  };

  const renderField = (
    field: keyof typeof contractData,
    type: "text" | "date" = "text",
    options?: string[],
    placeholder?: string,
  ) => {
    if (isEditing) {
      return (
        <div className="w-full">
          <Field
            path={field}
            value={contractData[field]}
            onUpdate={handleFieldUpdate}
            type={type}
            options={options}
            placeholder={placeholder || `Nhập ${field}`}
          />
        </div>
      );
    }

    const value = contractData[field];
    if (!value) return "---";
    if (type === "date") return dayjs(value).format("DD/MM/YYYY");
    if (field === "basicSalary")
      return `${Number(value).toLocaleString("vi-VN")} VND`;

    return value;
  };

  const columns = useMemo<TableEditColumn<ContractAllowanceRecord>[]>(
    () => [
      {
        title: "STT",
        key: "stt",
        dataIndex: "stt",
        width: 10,
        align: "left",
        render: (_, __, index: number) => index + 1,
      },
      {
        title: "Tên phụ cấp",
        dataIndex: "name",
        key: "name",
        editable: true,
      },
      {
        title: "Số tiền phụ cấp",
        dataIndex: "amount",
        key: "amount",
        editable: true,
        render: (value) => `${Number(value || 0).toLocaleString("vi-VN")} VND`,
      },
      {
        title: "Đơn vị tính",
        dataIndex: "unit",
        key: "unit",
        editable: true,
      },
      {
        title: "Ghi chú",
        dataIndex: "note",
        key: "note",
        editable: true,
      },
    ],
    [],
  );

  if (isFetching) {
    return (
      <div className="flex justify-center py-10">
        <Spin tip="Đang tải dữ liệu hợp đồng..." />
      </div>
    );
  }

  return (
    <div className="px-0 rounded-2xl relative">
      <div className="flex flex-col xl:flex-row justify-between items-stretch mb-6 w-full gap-6">
        {/* THÔNG TIN HỢP ĐỒNG */}
        <div className="bg-white py-3 pb-6 rounded-2xl w-full xl:w-[calc(60%-12px)] flex flex-col relative">
          <FourColumnRowSection
            leftTitle="Thông tin hợp đồng"
            rightTitle=""
            rows={[
              {
                leftLabel: "Số hợp đồng",
                leftValue: renderField(
                  "contractNumber",
                  "text",
                  undefined,
                  "Nhập số hợp đồng",
                ),
                rightLabel: "Ngày bắt đầu",
                rightValue: renderField(
                  "startDate",
                  "date",
                  undefined,
                  "Chọn ngày",
                ),
              },
              {
                leftLabel: "Loại hợp đồng",
                leftValue: renderField(
                  "contractType",
                  "text",
                  [
                    "HĐLĐ xác định thời gian",
                    "HĐLĐ không xác định thời gian",
                    "Thử việc",
                  ],
                  "Chọn loại HĐ",
                ),
                rightLabel: "Ngày kết thúc",
                rightValue: renderField(
                  "endDate",
                  "date",
                  undefined,
                  "Chọn ngày",
                ),
              },
              {
                leftLabel: "Ngày vào công ty",
                leftValue: renderField(
                  "joinDate",
                  "date",
                  undefined,
                  "Chọn ngày",
                ),
              },
            ]}
          />
          <FourColumnRowSection
            leftTitle="Thông tin công việc"
            rightTitle=""
            rows={[
              {
                leftLabel: "Vị trí",
                leftValue: renderField(
                  "position",
                  "text",
                  ["Nhân viên", "Quản lý", "Giám đốc"],
                  "Chọn vị trí",
                ),
                rightLabel: "Chức danh",
                rightValue: renderField(
                  "jobTitle",
                  "text",
                  ["Tài xế", "Phụ xe", "Thư ký"],
                  "Chọn chức danh",
                ),
              },
            ]}
          />
          <FourColumnRowSection
            leftTitle="Lương cơ bản"
            rightTitle=""
            rows={[
              {
                leftLabel: "Lương cơ bản",
                leftValue: renderField(
                  "basicSalary",
                  "text",
                  undefined,
                  "Nhập lương",
                ),
                rightLabel: "Yêu cầu làm việc",
                rightValue: renderField(
                  "workRequirement",
                  "text",
                  undefined,
                  "Nhập yêu cầu",
                ),
              },
            ]}
          />
        </div>

        {/* UPLOAD TÀI LIỆU */}
        <div className="w-full xl:w-[calc(40%-12px)]">
          <Col className="h-full">
            <Card className="!rounded-[16px] !border-none shadow-sm h-full flex flex-col">
              <span className="text-[16px] font-bold text-gray-500 block mb-6">
                Tệp tin hợp đồng
              </span>

              <div className="flex flex-col gap-[24px]">
                <UploadFileCommon
                  filesData={filesData}
                  setFilesData={setFilesData}
                  isDisabled={!isEditing}
                  readOnly={!isEditing}
                  maxCount={6}
                  maxSizeMB={2}
                  accept="image/*,.pdf"
                />

                {/* GHI CHÚ */}
                <div className="flex flex-col gap-2 mt-4">
                  <Text className="text-[14px] font-bold text-gray-500">
                    Ghi chú
                  </Text>
                  {isEditing ? (
                    <TextArea
                      rows={4}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="!rounded-[16px] !p-4 !bg-white !border-gray-200 focus:!border-blue-400"
                      placeholder="Nhập ghi chú..."
                    />
                  ) : (
                    <div className="p-4 rounded-[16px] bg-gray-50 border border-gray-100 min-h-[100px]">
                      {note || "Không có ghi chú"}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </div>
      </div>

      <div className="bg-white py-4 rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <Title level={5} className="!mb-4 pl-6 uppercase text-gray-500">
          Phụ cấp thêm
        </Title>
        <div className="px-5">
          <TableEditCommon<ContractAllowanceRecord>
            columns={columns}
            data={allowanceData.map((item, index) => ({
              ...item,
              key: item.id || `temp-${index}`,
            }))}
            onChangeData={handleChangeData}
            onSave={handleSaveData}
            onDeleteRow={handleDeleteRow}
            addRowText="Thêm phụ cấp mới"
            bordered
            editable={true}
          />
        </div>
      </div>
    </div>
  );
  },
);

ContractInfoTab.displayName = "ContractInfoTab";

export default ContractInfoTab;
